import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getDebtPointPaymentsThunk,
    getEmployeePositionGetCurrentPosition,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { UserRole } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks";
import { getKpiTargetDate } from "@/utils";

export const useMemberInfoData = () => {
    const dispatch = useAppDispatch();
    const { userId } = useParams();

    const id = useAppSelector((state) => state.user.id);
    const team = useAppSelector((state) => state.manager.team);
    const routeList = useAppSelector((state) => state.manager.routeList);

    const routeId = useMemo(
        () =>
            Object.values(routeList).find(
                (route) => route.agentInfo.id === userId,
            )?.uuid,
        [routeList, userId],
    );

    const member = useMemo(
        () => team?.members?.find((member) => member.userId === userId),
        [team?.members, userId],
    );

    const [dashboardLoaded, setDashboardLoaded] = useState(false);
    const [employeeId, setEmployeeId] = useState<string>("");

    const today = dayjs().format(backendDateFormat.date);

    const loadDashboardData = useCallback(async () => {
        try {
            const kpiTargetDate = getKpiTargetDate();

            const { result: employee } =
                await getEmployeePositionGetCurrentPosition({
                    role: UserRole.Ta,
                    userId,
                });
            setEmployeeId(employee.id);

            const commonKpiParams = {
                employeeId: employee.id,
                targetDate: kpiTargetDate,
                roleSlug: UserRole.Ta,
            };

            const requests: Promise<unknown>[] = [
                dispatch(getUserTeamGetByManagerThunk({ managerId: id })),
                dispatch(getKpiGetSalaryToPayThunk(commonKpiParams)),
                dispatch(getKpiGetProgressThunk(commonKpiParams)),
            ];

            if (routeId) {
                requests.push(
                    dispatch(
                        getDebtPointPaymentsThunk({
                            routeId,
                            targetDate: today,
                        }),
                    ),
                );
            }

            await Promise.all(requests);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setDashboardLoaded(true);
        }
    }, [userId, dispatch, id, routeId, today]);

    useEffect(() => {
        setDashboardLoaded(false);
        loadDashboardData();
    }, [userId, id, dispatch, loadDashboardData]);

    const values = useMemo(() => {
        return {
            member,
            routeId,
            employeeId,
            dashboardLoaded,
        };
    }, [member, routeId, employeeId, dashboardLoaded]);

    return values;
};
